import React, { useMemo } from "react";
import { EmptyState } from "../../../../common/comms/EmptyState";
import { formatDuration } from "../../../../utils/oversightFormat";
import { SessionKpiRow } from "./SessionKpiRow";
import { viewerTimeZone, zoneAbbreviation, isValidTimeZone } from "./viewerZone";
import {
  SESSION_END,
  buildSessionDays,
  summariseSessionDays,
  trackWindow,
  trackPercent,
  formatClockHour,
  zonedHour,
  zonedDateKey,
} from "./sessionTracks";

/** Block fill/edge per end kind. Full class strings so Tailwind can see them. */
const END_STYLE = {
  [SESSION_END.CLEAN]: {
    className: "bg-coach-1-bg border-l-2 border-coach-1",
    label: "Signed out normally",
    swatch: "bg-coach-1-bg border-l-2 border-coach-1",
  },
  [SESSION_END.ESTIMATED]: {
    className: "bg-internal-bg border-l-2 border-internal",
    label: "End time estimated",
    swatch: "bg-internal-bg border-l-2 border-internal",
  },
  [SESSION_END.OPEN]: {
    className: "bg-success-bg border-l-2 border-success",
    label: "Still signed in",
    swatch: "bg-success-bg border-l-2 border-success",
  },
};

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * "2026-08-23" → "Sat 23 Aug". Parsed as UTC to match the bucket, and manually
 * rather than via locale formatting so the label cannot drift a day.
 * @param {string} date
 * @returns {string}
 */
function formatDayLabel(date) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || "");
  if (!m) return date || "";
  const [, y, mo, d] = m;
  const weekday = WEEKDAY_ABBR[new Date(Date.UTC(+y, +mo - 1, +d)).getUTCDay()];
  return `${weekday} ${Number(d)} ${MONTH_ABBR[+mo - 1]}`;
}

/** The hour ruler above the tracks; every third hour is labelled. */
function HourAxis({ window: axis }) {
  const hours = [];
  for (let h = axis.startHour; h <= axis.endHour; h += 1) hours.push(h);
  return (
    <div className="relative h-3" aria-hidden="true">
      {hours.map((h) => {
        if (h % 3 !== 0) return null;
        const left = trackPercent(h, axis);
        // The end labels would half-hang off the track if they were centred.
        const nudge = left === 0 ? "" : left === 100 ? "-translate-x-full" : "-translate-x-1/2";
        return (
          <span
            key={h}
            className={`absolute text-[9px] text-gray-400 ${nudge}`}
            style={{ left: `${left}%` }}
          >
            {String(h).padStart(2, "0")}:00
          </span>
        );
      })}
    </div>
  );
}

/**
 * Tooltip for one block: how it ended, when, and in which zone. The coach's own
 * clock is appended only once the API supplies their zone.
 */
function blockTitle(s, zone, zoneAbbr, coachZone, coachAbbr) {
  const style = END_STYLE[s.kind];
  const clock = `${formatClockHour(s.startHour)}–${formatClockHour(s.endHour)}`;
  let title = zoneAbbr ? `${style.label} · ${clock} ${zoneAbbr}` : `${style.label} · ${clock}`;

  if (s.startDayShift !== 0 || s.wrapsMidnight) {
    const localDate = zonedDateKey(s.startIso, zone);
    if (localDate) title += ` on ${localDate} where you are`;
  }
  if (coachZone) {
    const start = formatClockHour(zonedHour(s.startIso, coachZone));
    const end = formatClockHour(zonedHour(s.endIso, coachZone));
    title += ` · ${start}–${end}${coachAbbr ? ` ${coachAbbr}` : ""} for the coach`;
  }
  if (s.platform) title += ` on ${s.platform}`;
  return title;
}

/** Chips for blocks that land on an adjacent date in the viewer's zone. */
function dayShiftNotes(day) {
  const ahead = day.sessions.filter(
    (s) => s.startDayShift > 0 || s.endDayShift > 0 || s.wrapsMidnight
  ).length;
  const behind = day.sessions.filter((s) => s.startDayShift < 0 || s.endDayShift < 0).length;
  const notes = [];
  if (ahead > 0) notes.push(`${ahead} block${ahead === 1 ? "" : "s"} on the next date`);
  if (behind > 0) notes.push(`${behind} block${behind === 1 ? "" : "s"} on the previous date`);
  return notes;
}

/** One day: label + duration on the left, the hour track, platform/notes on the right. */
function SessionDayRow({ day, window: axis, zone, zoneAbbr, coachZone, coachAbbr }) {
  const notes = [];
  if (day.count > 2) notes.push(`${day.count} times signed back in`);
  if (day.estimatedCount > 0) {
    notes.push(`${day.estimatedCount} estimated end${day.estimatedCount === 1 ? "" : "s"}`);
  }
  if (day.hasDayShift) notes.push(...dayShiftNotes(day));
  if (day.truncated) notes.push("more sessions than shown");

  const lastBlock = day.sessions[day.sessions.length - 1];

  return (
    <div
      className={`grid grid-cols-[9rem_1fr] lg:grid-cols-[9rem_1fr_9rem] gap-3 lg:gap-4 items-center px-4 py-2.5 border-t border-gray-100 ${
        day.isToday ? "bg-ca-today" : ""
      }`}
    >
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">
          {formatDayLabel(day.date)}
          {day.isToday && (
            <span className="ml-1.5 px-1.5 py-px rounded-full text-[10px] font-semibold bg-light-orange text-orange-primary">
              today
            </span>
          )}
        </p>
        <p className="mt-0.5 text-[11px] text-gray-400 whitespace-nowrap">
          {formatDuration(day.totalSeconds)} online · {day.count}{" "}
          {day.count === 1 ? "session" : "sessions"}
        </p>
      </div>

      <div className="relative h-6 rounded-md bg-rail-bg border border-gray-100">
        {day.sessions.map((s, i) => {
          const left = trackPercent(s.startHour, axis);
          const width = Math.max(0.6, trackPercent(s.endHour, axis) - left);
          const style = END_STYLE[s.kind];
          return (
            <div
              key={`${day.date}-${i}`}
              title={blockTitle(s, zone, zoneAbbr, coachZone, coachAbbr)}
              className={`absolute top-1 bottom-1 rounded ${style.className}`}
              style={{ left: `${left}%`, width: `${width}%` }}
            />
          );
        })}
        {day.hasOpen && lastBlock && (
          <span
            aria-hidden="true"
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-success ring-2 ring-white animate-live-pulse"
            style={{ left: `${trackPercent(lastBlock.endHour, axis)}%` }}
          />
        )}
      </div>

      <div className="hidden lg:flex flex-wrap gap-1 justify-end">
        {day.platforms.map((p) => (
          <span key={p} className="px-1.5 py-0.5 rounded text-[10px] text-gray-700 bg-gray-100">
            {p}
          </span>
        ))}
        {notes.map((n) => (
          <span
            key={n}
            className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-internal-text bg-internal-bg whitespace-nowrap"
          >
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Presence, day by day — the Sessions tab.
 *
 * Replaces the raw connect/disconnect log, which read as noise. Same-day
 * sessions are grouped onto one track: colour says how the session ended
 * (clean, gap-filled by the stale sweep, still open), width says how long.
 * Reads `series[].sessions[]` straight off the analytics payload the page
 * already fetched — no second request.
 *
 * TIMEZONE: clock positions and labels are in the viewer's own zone, named at
 * the head of the hour ruler. Rows and their totals stay UTC day buckets,
 * because `online_seconds` is overlap-clamped to that bucket and cannot be
 * re-attributed — so a block whose local date differs from its row's is
 * labelled rather than moved.
 *
 * Weekly granularity is refused rather than drawn: seven days cannot share one
 * 24-hour clock without lying about when the coach worked.
 *
 * @param {Object} props
 * @param {Array<Object>|null|undefined} props.series - `series[]` from the coach analytics payload.
 * @param {'day'|'week'} props.granularity - Bucket size the series is grouped by.
 * @param {string} [props.timeZone] - Overrides the viewer's zone. A test seam; the app omits it.
 * @param {string|null} [props.coachTimeZone] - The coach's IANA zone, once the API supplies one.
 *   Absent today, so the coach's own clock renders nowhere.
 */
export function SessionDays({ series, granularity, timeZone, coachTimeZone = null }) {
  const zone = useMemo(() => timeZone || viewerTimeZone(), [timeZone]);
  const zoneAbbr = useMemo(() => zoneAbbreviation(new Date(), zone), [zone]);

  // Inert until the payload carries a coach zone: no element, no second layout.
  // Showing the coach their own clock twice would be noise, hence the !== zone.
  const coachZone = useMemo(
    () => (isValidTimeZone(coachTimeZone) && coachTimeZone !== zone ? coachTimeZone : null),
    [coachTimeZone, zone]
  );
  const coachAbbr = useMemo(
    () => (coachZone ? zoneAbbreviation(new Date(), coachZone) : ""),
    [coachZone]
  );

  const days = useMemo(
    () => buildSessionDays(series, { granularity, timeZone: zone }),
    [series, granularity, zone]
  );
  const summary = useMemo(() => summariseSessionDays(days), [days]);
  const axis = useMemo(() => trackWindow(days), [days]);
  const anyDayShift = useMemo(() => days.some((d) => d.hasDayShift), [days]);

  if (granularity !== "day") {
    return (
      <EmptyState
        title="Switch to Day to see presence"
        hint="A weekly bucket spans seven different clocks, so it can't be drawn on one 24-hour track."
      />
    );
  }

  if (days.length === 0) {
    return <EmptyState title="No presence recorded in this period" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2.5">
        <h2 className="text-base font-bold text-gray-900">Presence, day by day</h2>
        <span className="text-xs text-gray-500">What their working day actually looks like</span>
        <span className="sm:ml-auto text-[11px] text-gray-400">
          Time signed in, accurate to about a minute. Not a timesheet.
        </span>
      </div>

      <SessionKpiRow summary={summary} zoneAbbr={zoneAbbr} />

      <div className="flex flex-wrap gap-4">
        {Object.values(SESSION_END).map((kind) => (
          <span key={kind} className="inline-flex items-center gap-1.5 text-[11px] text-gray-700">
            <span className={`w-4 h-2.5 rounded-sm ${END_STYLE[kind].swatch}`} aria-hidden="true" />
            {END_STYLE[kind].label}
          </span>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
        <div className="min-w-[36rem]">
          <div className="grid grid-cols-[9rem_1fr] lg:grid-cols-[9rem_1fr_9rem] gap-3 lg:gap-4 px-4 pt-2.5">
            {/* Sits at the head of the ruler so the hours below can't be read without it. */}
            <span className="self-end text-right text-[9px] font-semibold text-gray-500 leading-3">
              {zoneAbbr}
            </span>
            <HourAxis window={axis} />
            <span className="hidden lg:block" />
          </div>
          {days.map((day) => (
            <SessionDayRow
              key={day.date}
              day={day}
              window={axis}
              zone={zone}
              zoneAbbr={zoneAbbr}
              coachZone={coachZone}
              coachAbbr={coachAbbr}
            />
          ))}
        </div>
      </div>

      <p className="text-[11px] text-gray-400">
        Newest first. Clock positions are in your timezone, {zone}
        {zoneAbbr ? ` (${zoneAbbr})` : ""}. Each row totals one UTC calendar day. Where the app
        never reported a sign-out, the end is estimated — those blocks are amber.
        {anyDayShift &&
          " Blocks tagged as landing on another date fall there in your timezone; they stay on the UTC day the row totals."}
      </p>
    </div>
  );
}

export default SessionDays;
